import { CROP_JPEG_QUALITY, type ImageCropSpec } from '@/lib/constants/image-crop';

/** The crop rectangle react-easy-crop reports, in the source image's own pixels. */
export interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface OutputSize {
  width: number;
  height: number;
}

/**
 * The size a cropped image is written at: the spec's target, or the crop's own
 * size when that is smaller. A crop is only ever scaled DOWN, never up - blowing a
 * small image up to the target would add bytes and no detail (FR-121).
 *
 * The height always follows the spec's aspect from the chosen width, so the saved
 * image has the locked proportions however the crop rounded.
 *
 * @param crop - the selected area, in source pixels
 * @param spec - the locked frame for this upload kind
 */
export function computeOutputSize(crop: PixelCrop, spec: ImageCropSpec): OutputSize {
  const width = Math.max(1, Math.min(Math.round(crop.width), spec.outputWidth));
  return { width, height: Math.max(1, Math.round(width / spec.aspect)) };
}

/** PNG stays PNG (lossless, keeps transparency); everything else is written as JPEG. */
export function outputMimeType(sourceType: string): 'image/png' | 'image/jpeg' {
  return sourceType === 'image/png' ? 'image/png' : 'image/jpeg';
}

/** True when a decoded image is too big to crop safely. */
export function exceedsPixelLimit(width: number, height: number, maxPixels: number): boolean {
  return width * height > maxPixels;
}

/** Loads an image file into an element, rejecting when the browser cannot decode it. */
export function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('IMAGE_UNREADABLE'));
    image.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('CANVAS_EXPORT_FAILED'))),
      type,
      type === 'image/jpeg' ? CROP_JPEG_QUALITY : undefined,
    );
  });
}

/**
 * Draws the chosen area of `image` onto a canvas at the output size and returns it
 * as a `File` with the source file's name and (PNG or JPEG) type.
 *
 * Drawing from an `<img>` applies the photo's EXIF rotation in current browsers, so
 * a phone photo is cropped the way it is displayed.
 *
 * @throws Error `CANVAS_EXPORT_FAILED` or `CANVAS_UNAVAILABLE` when the browser cannot produce the file
 */
export async function cropImageToFile(
  image: HTMLImageElement,
  crop: PixelCrop,
  spec: ImageCropSpec,
  source: File,
): Promise<File> {
  const { width, height } = computeOutputSize(crop, spec);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('CANVAS_UNAVAILABLE');
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, width, height);

  const type = outputMimeType(source.type);
  const blob = await canvasToBlob(canvas, type);
  return new File([blob], source.name, { type, lastModified: Date.now() });
}
